import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { API_BASE_URL } from '../api/apiClient';

let appInsights: ApplicationInsights | null = null;
let initialized = false;

export const initializeApplicationInsights = async () => {
  if (initialized) return;
  initialized = true;

  try {
    const response = await fetch(`${API_BASE_URL}/api/telemetry/client-config`, {
      cache: 'no-store',
      credentials: 'include',
    });
    if (!response.ok) return;

    const payload = await response.json();
    const config = payload?.data || payload;
    if (!config?.enabled || !config?.connectionString) return;

    appInsights = new ApplicationInsights({
      config: {
        connectionString: config.connectionString,
        enableAutoRouteTracking: false,
        enableCorsCorrelation: true,
        disableFetchTracking: false,
        disableAjaxTracking: false,
        autoTrackPageVisitTime: true,
        // Chromium blocks the deprecated `unload` event under the app's
        // Permissions-Policy. The SDK continues to flush through pagehide and
        // visibilitychange, so disabling only `unload` removes the warning
        // without dropping normal page-lifecycle telemetry.
        disablePageUnloadEvents: ['unload'],
      },
    });
    appInsights.loadAppInsights();

    window.addEventListener('error', event => {
      if (event.error) trackException(event.error);
    });

    window.addEventListener('unhandledrejection', event => {
      const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
      trackException(reason);
    });
  } catch {
    // Telemetry must never block the application.
  }
};

export const trackPageView = (path: string) => {
  appInsights?.trackPageView({ name: document.title || path, uri: window.location.href });
};

export const trackException = (error: Error) => {
  appInsights?.trackException({ exception: error });
};

export const trackMetric = (name: string, value: number, properties?: Record<string, string>) => {
  appInsights?.trackMetric({ name, average: value }, properties);
};

export const setTelemetryUser = (userId?: string | null) => {
  if (!appInsights) return;
  if (userId) appInsights.setAuthenticatedUserContext(userId);
  else appInsights.clearAuthenticatedUserContext();
};
